import { groupMediaByMonth, monthLabelOf } from "@couples/shared";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CheckCheck,
  Download,
  Image as ImageIcon,
  ImagePlus,
  SquareMousePointer,
  Star,
  Tag as TagIcon,
  Trash2,
  UserRound,
  Video,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import MediaTile, { TileFavoriteStar, TileMarks, VideoBadge } from "../components/MediaTile";
import MonthSheet from "../components/MonthSheet";
import QueryState from "../components/QueryState";
import TagPicker from "../components/TagPicker";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { useMediaDownload } from "../lib/media/download";
import { fetchExportItems } from "../lib/media/export";
import { addFavorites, fetchProtectedIds } from "../lib/media/favorites";
import { fetchMembers } from "../lib/media/members";
import { RETENTION_DAYS } from "../lib/media/retention";
import { fetchTags, softDeleteMedia } from "../lib/media/tags";
import {
  countTimeline,
  fetchMonthCounts,
  fetchTimelinePage,
  type MediaTypeFilter,
  type TimelineCursor,
  type TimelineFilter,
  type UploaderFilter,
} from "../lib/media/timeline";
import { useSignedUrls } from "../lib/media/useSignedUrls";
import { useMediaTasks } from "../lib/mediaTasks";
import { queryKeys } from "../lib/queryKeys";
import { useLoadMoreSentinel } from "../lib/useLoadMoreSentinel";
import { useSelection } from "../lib/useSelection";
import { useToast } from "../lib/toast";
import MediaDetail from "./MediaDetail";

function chipClass(active: boolean): string {
  return active
    ? "bg-[var(--color-teal)] text-white"
    : "border border-[var(--color-line)] text-[var(--color-sub)]";
}

/**
 * 0件のときの文言。
 *
 * 絞り込みの軸(月・種別・追加した人・タグ)は互いに独立して効くため、
 * 「何を絞ったせいで空なのか」を狭いほうから順に選ぶ。
 */
function emptyMessageOf(
  filter: TimelineFilter,
  mediaType: MediaTypeFilter,
  month: string | null,
  uploaderName: string | undefined,
): string {
  if (month !== null) return "この月に写真・動画はありません。";
  if (mediaType !== null)
    return `条件に合う${mediaType === "photo" ? "写真" : "動画"}はありません。`;
  if (uploaderName !== undefined) return `${uploaderName}が追加した写真・動画はありません。`;
  if (filter.kind === "all") return "まだ写真がありません。";
  if (filter.kind === "favorite") return "お気に入りはまだありません。";
  if (filter.kind === "untagged") return "タグの付いていない写真・動画はありません。";
  return "このタグの写真・動画はありません。";
}

interface Props {
  onPickFiles: () => void;
  /** アップロードできない状態(カップル未設定、送信中、ピッカー待ち) */
  uploadDisabled: boolean;
  /** 選択モード。PCのヘッダーとスマホの右下ボタンの両方から切り替えるため親が持つ */
  selectMode: boolean;
  onSelectModeChange: (on: boolean) => void;
}

export default function Timeline({
  onPickFiles,
  uploadDisabled,
  selectMode,
  onSelectModeChange,
}: Props) {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const download = useMediaDownload();
  const { exporting } = useMediaTasks();
  const [tagging, setTagging] = useState(false);
  const [picking, setPicking] = useState(false);
  const [filter, setFilter] = useState<TimelineFilter>({ kind: "all" });
  // 写真だけ・動画だけ。タグや月の起点とは独立に効く
  const [mediaType, setMediaType] = useState<MediaTypeFilter>(null);
  // 選んだ月。その月の写真・動画だけを表示する
  const [month, setMonth] = useState<string | null>(null);
  // 誰が追加したか。これもタグ・月・種別とは独立に効く
  const [uploader, setUploader] = useState<UploaderFilter>(null);
  const { selected, toggle, selectAll, clear } = useSelection();
  const [detailId, setDetailId] = useState<string | null>(null);

  const timeline = useInfiniteQuery({
    queryKey: queryKeys.timeline(filter, mediaType, month, uploader),
    queryFn: ({ pageParam }) => fetchTimelinePage(pageParam, filter, mediaType, month, uploader),
    initialPageParam: null as TimelineCursor | null,
    getNextPageParam: (page) => page.next,
  });

  const monthCounts = useQuery({ queryKey: queryKeys.monthCounts, queryFn: fetchMonthCounts });
  const tags = useQuery({ queryKey: queryKeys.tags, queryFn: fetchTags });
  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers });

  // 詳細表示の「n / 全体」用。読み込み済み件数ではなく絞り込み後の総数を出す
  const total = useQuery({
    queryKey: queryKeys.timelineCount(filter, mediaType, month, uploader),
    queryFn: () => countTimeline(filter, mediaType, month, uploader),
  });

  // 絞り込みのチップ。自分を先に出す(自分が追加したぶんを探すことのほうが多い)
  const selfId = session?.user.id;
  const uploaders = useMemo(
    () =>
      [...(members.data ?? new Map<string, string>())]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => Number(b.id === selfId) - Number(a.id === selfId)),
    [members.data, selfId],
  );

  const items = useMemo(
    () => timeline.data?.pages.flatMap((page) => page.items) ?? [],
    [timeline.data],
  );
  const groups = useMemo(
    // グルーピングは takenAt しか見ないため、列名だけ合わせて渡す
    () => groupMediaByMonth(items.map((item) => ({ ...item, takenAt: item.taken_at }))),
    [items],
  );

  // 0件のときに「選択を解除」と出さないよう、読み込み済みがあることまで見る
  const allSelected = items.length > 0 && selected.size === items.length;

  // 署名付きURLは TTL 1時間。期限切れは表示できなかった時点で取り直す
  const thumbKeys = useMemo(() => items.map((i) => i.thumbnail_key), [items]);
  const { urls: thumbUrls, refresh: refreshThumbs } = useSignedUrls(thumbKeys);

  // 末尾が見えたら次ページを取りに行く
  const sentinel = useLoadMoreSentinel(timeline);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = timeline;

  // 詳細表示で末尾近くまで送ったときも同じように足す
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  function leaveSelectMode() {
    onSelectModeChange(false);
    clear();
  }

  // ヘッダー(親)側で選択モードを切ったときも、選んだ跡を残さない
  useEffect(() => {
    if (!selectMode && selected.size > 0) clear();
  }, [selectMode, selected, clear]);

  async function refreshAll() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
    await queryClient.invalidateQueries({ queryKey: queryKeys.monthCounts });
    await queryClient.invalidateQueries({ queryKey: queryKeys.tags });
  }

  async function deleteSelected() {
    const chosen = [...selected];
    if (chosen.length === 0) return;

    // お気に入りに入っているものは消さない。相手が入れたものも含めて守る
    let protectedIds: Set<string>;
    try {
      protectedIds = await fetchProtectedIds(chosen);
    } catch (e) {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error("お気に入り保護の確認に失敗", e);
      toast(`${String(chosen.length)}件の削除に失敗しました。`, "error");
      return;
    }
    const ids = chosen.filter((id) => !protectedIds.has(id));
    if (ids.length === 0) {
      toast("お気に入りに追加されているため、削除できません。", "error");
      return;
    }

    // 守られているぶんは黙って外さず、何件を残すのかを確認の場で伝える
    const skipped =
      protectedIds.size > 0
        ? `\n${String(protectedIds.size)}件はお気に入りに追加されているため、スキップします。`
        : "";
    const ok = await confirm({
      message: `${String(ids.length)}件を削除します。${skipped}\n※${String(RETENTION_DAYS)}日以内ならゴミ箱から復元できます。`,
      confirmLabel: "削除",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await softDeleteMedia(ids);
      leaveSelectMode();
      await refreshAll();
      toast(`${String(ids.length)}件を削除しました。`);
    } catch (e) {
      console.error("削除に失敗", e);
      toast(`${String(ids.length)}件の削除に失敗しました。`, "error");
    }
  }

  /**
   * 選んだぶんを端末に保存する。
   * 全件の保存(→ Album のメニュー)と同じ流れを通す。ZIP になるのは2件以上のとき。
   */
  async function downloadSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    // 取りやめたときは選択を残す。選び直しからやり直させない
    if (await download(() => fetchExportItems(ids))) leaveSelectMode();
  }

  async function favoriteSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    // まとめて足すと取り消しに手間が掛かるので、件数を見せてから実行する。
    // 既にお気に入りのものは upsert が黙って弾くため、件数は「選んだ数」を指す
    const ok = await confirm({
      message: `${String(ids.length)}件をお気に入りに追加します。`,
      confirmLabel: "追加",
    });
    if (!ok) return;
    try {
      await addFavorites(ids);
      leaveSelectMode();
      await queryClient.invalidateQueries({ queryKey: queryKeys.favorites });
      toast(`${String(ids.length)}件をお気に入りに追加しました。`);
    } catch (e) {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error("お気に入りの追加に失敗", e);
      toast(`${String(ids.length)}件のお気に入りの追加に失敗しました。`, "error");
    }
  }

  if (timeline.isPending || timeline.isError) {
    return (
      <QueryState
        isPending={timeline.isPending}
        isError={timeline.isError}
        errorMessage="写真・動画の取得に失敗しました。"
        className="p-6 text-sm"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* 月の起点とタグの絞り込みは別のもの。行を分けて、互いに影響しないようにする。
          月・種別・追加した人はチップが増えるため、狭い画面では折り返す */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => {
            setPicking(true);
          }}
          className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs ${chipClass(
            month !== null,
          )}`}
        >
          <CalendarDays className="size-3" aria-hidden />
          {month === null ? "月を選ぶ" : monthLabelOf(month)}
        </button>
        {month !== null && (
          <button
            type="button"
            onClick={() => {
              setMonth(null);
              window.scrollTo({ top: 0 });
            }}
            aria-label="月の指定を解除"
            className="flex size-6 items-center justify-center rounded-full border border-[var(--color-line)] text-[var(--color-sub)]"
          >
            <X className="size-3" aria-hidden />
          </button>
        )}

        {/* 種別の絞り込み。もう一度押すと解除 */}
        <button
          type="button"
          onClick={() => {
            setMediaType((current) => (current === "photo" ? null : "photo"));
          }}
          aria-pressed={mediaType === "photo"}
          className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs ${chipClass(
            mediaType === "photo",
          )}`}
        >
          <ImageIcon className="size-3" aria-hidden />
          写真
        </button>
        <button
          type="button"
          onClick={() => {
            setMediaType((current) => (current === "video" ? null : "video"));
          }}
          aria-pressed={mediaType === "video"}
          className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs ${chipClass(
            mediaType === "video",
          )}`}
        >
          <Video className="size-3" aria-hidden />
          動画
        </button>

        {/* 誰が追加したか。二人ぶんを並べ、もう一度押すと解除 */}
        {uploaders.map((member) => (
          <button
            key={member.id}
            type="button"
            onClick={() => {
              setUploader((current) => (current === member.id ? null : member.id));
            }}
            aria-pressed={uploader === member.id}
            aria-label={`${member.name}が追加したものだけ表示`}
            className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs ${chipClass(
              uploader === member.id,
            )}`}
          >
            <UserRound className="size-3" aria-hidden />
            {member.name}
          </button>
        ))}

        {/* PC の追加・選択の入口。スマホ・タブレットは右下の丸ボタンが受け持つ */}
        <div className="ml-auto hidden items-center gap-2 xl:flex">
          <button
            type="button"
            onClick={() => {
              if (selectMode) leaveSelectMode();
              else onSelectModeChange(true);
            }}
            aria-pressed={selectMode}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition ${
              selectMode
                ? "bg-[var(--color-sub)] font-medium text-[var(--color-bg)] hover:opacity-85"
                : "border border-[var(--color-line)] bg-white text-[var(--color-ink)] hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
            }`}
          >
            <SquareMousePointer className="size-4" aria-hidden />
            {selectMode ? "選択をやめる" : "選択"}
          </button>
          <button
            type="button"
            onClick={onPickFiles}
            disabled={uploadDisabled}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-white transition enabled:hover:opacity-85 disabled:opacity-50"
          >
            <ImagePlus className="size-4" aria-hidden />
            追加
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => {
            // 月の起点は別軸なので触らない
            setFilter({ kind: "all" });
          }}
          className={`shrink-0 rounded-full px-3 py-1 text-xs ${chipClass(filter.kind === "all")}`}
        >
          すべて
        </button>
        <button
          type="button"
          onClick={() => {
            setFilter({ kind: "untagged" });
          }}
          className={`shrink-0 rounded-full px-3 py-1 text-xs ${chipClass(
            filter.kind === "untagged",
          )}`}
        >
          タグなし
        </button>
        {tags.data?.map((tag) => (
          <button
            key={tag.id}
            type="button"
            onClick={() => {
              setFilter({ kind: "tag", id: tag.id });
            }}
            className={`shrink-0 rounded-full px-3 py-1 text-xs ${chipClass(
              filter.kind === "tag" && filter.id === tag.id,
            )}`}
          >
            {tag.name}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-4 p-10 text-center">
          <p className="text-sm text-[var(--color-sub)]">
            {emptyMessageOf(
              filter,
              mediaType,
              month,
              uploader === null ? undefined : members.data?.get(uploader),
            )}
          </p>
          {filter.kind === "all" && uploader === null && (
            <button
              type="button"
              onClick={onPickFiles}
              className="flex items-center gap-2 rounded-xl bg-[var(--color-teal)] px-5 py-3 text-sm font-medium text-white"
            >
              <ImagePlus className="size-5" aria-hidden />
              写真・動画を追加
            </button>
          )}
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.month} className="flex flex-col gap-2">
            <header className="flex items-center gap-2">
              <span className="text-sm font-medium">{monthLabelOf(group.month)}</span>
              <span className="h-px flex-1 bg-[var(--color-line)]" />
              <span className="text-[11px] text-[var(--color-sub)]">
                {filter.kind === "all" && mediaType === null && uploader === null
                  ? (monthCounts.data?.get(group.month) ?? group.items.length)
                  : group.items.length}
                件
              </span>
            </header>

            <div className="grid grid-cols-3 gap-1.5 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
              {group.items.map((media) => (
                <MediaTile
                  key={media.id}
                  thumbUrl={thumbUrls[media.thumbnail_key]}
                  onThumbError={refreshThumbs}
                  showCheck={selectMode}
                  isSelected={selected.has(media.id)}
                  onClick={() => {
                    if (selectMode) toggle(media.id);
                    else setDetailId(media.id);
                  }}
                >
                  {media.type === "video" && (
                    <VideoBadge durationSeconds={media.duration_seconds} />
                  )}
                  <TileMarks mediaId={media.id} />
                  <TileFavoriteStar mediaId={media.id} />
                </MediaTile>
              ))}
            </div>
          </section>
        ))
      )}

      <div ref={sentinel} className="h-8" aria-hidden />
      {isFetchingNextPage && (
        <p className="pb-4 text-center text-xs text-[var(--color-sub)]">読み込み中...</p>
      )}

      {/*
        追加と選択の入口。片手で持ったときに親指が届く右下に縦に積む。
        選んだ後も「選択をやめる」はこの丸ボタンが持ち続ける(下の操作バーは
        操作だけを並べる)ので、バーが出ているぶんだけ上に逃がす。
        マウス中心の PC(xl〜)ではヘッダーのボタンが入口になるので出さない。
      */}
      <div
        className={`pointer-events-none fixed inset-x-0 z-40 mx-auto flex max-w-[var(--content-w)] flex-col items-end gap-3 px-4 xl:hidden ${
          selectMode
            ? "bottom-[calc(5rem+env(safe-area-inset-bottom))]"
            : "bottom-[calc(4rem+env(safe-area-inset-bottom))]"
        }`}
      >
        {!selectMode && (
          <button
            type="button"
            onClick={onPickFiles}
            disabled={uploadDisabled}
            aria-label="写真・動画を追加"
            className="pointer-events-auto flex size-14 items-center justify-center rounded-full bg-[var(--color-teal)] text-white shadow-lg disabled:opacity-50"
          >
            <ImagePlus className="size-6" aria-hidden />
          </button>
        )}
        {items.length > 0 && (
          <button
            type="button"
            onClick={() => {
              if (selectMode) leaveSelectMode();
              else onSelectModeChange(true);
            }}
            aria-pressed={selectMode}
            aria-label={selectMode ? "選択をやめる" : "選択"}
            className={`pointer-events-auto flex size-14 items-center justify-center rounded-full shadow-lg ${
              selectMode
                ? "bg-[var(--color-sub)] text-[var(--color-bg)]"
                : "border border-[var(--color-line)] bg-white text-[var(--color-ink)]"
            }`}
          >
            {selectMode ? (
              <X className="size-6" aria-hidden />
            ) : (
              <SquareMousePointer className="size-6" aria-hidden />
            )}
          </button>
        )}
      </div>

      {selectMode && (
        // 下部ナビゲーションを覆う位置に出る。`fixed` には body のセーフエリア用
        // パディングが効かないため、自分で下側を空ける。
        // 選択モードに入った時点から出しておく。0件のうちは押せる操作が
        // 「すべて選択」だけになるが、置き場が動かないほうが迷わない。
        // 幅の限られるスマホ・タブレットでは、選んだ後の操作はアイコンだけにして
        // 「すべて選択」の文言を残す(何件に効くのかが読めるほうが大事なため)
        <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-center gap-1.5 border-t border-[var(--color-line)] bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] xl:gap-2 xl:left-56">
          <span className="text-xs text-[var(--color-sub)] tabular-nums">{selected.size}件</span>
          <button
            type="button"
            onClick={() => {
              // 全件選んだ状態でもう一度押すと、選び直しやすいよう全解除に切り替わる
              if (allSelected) clear();
              else selectAll(items.map((item) => item.id));
            }}
            className="flex shrink-0 items-center gap-1 rounded-lg border border-[var(--color-line)] px-2.5 py-2 text-sm transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)] xl:px-3"
          >
            <CheckCheck className="size-4" aria-hidden />
            {allSelected ? "選択を解除" : "すべて選択"}
          </button>
          <button
            type="button"
            onClick={() => void favoriteSelected()}
            disabled={selected.size === 0}
            aria-label="お気に入りに追加"
            className="flex shrink-0 items-center gap-1 rounded-lg border border-[var(--color-line)] px-2.5 py-2 text-sm transition-colors disabled:opacity-50 xl:px-3 enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
          >
            <Star className="size-4" aria-hidden />
            <span className="hidden xl:inline">お気に入りに追加</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setTagging(true);
            }}
            disabled={selected.size === 0}
            aria-label="タグを追加"
            className="flex shrink-0 items-center gap-1 rounded-lg border border-[var(--color-line)] px-2.5 py-2 text-sm transition-colors disabled:opacity-50 xl:px-3 enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
          >
            <TagIcon className="size-4" aria-hidden />
            <span className="hidden xl:inline">タグを追加</span>
          </button>
          <button
            type="button"
            onClick={() => void downloadSelected()}
            disabled={selected.size === 0 || exporting !== null}
            aria-label="ダウンロード"
            className="flex shrink-0 items-center gap-1 rounded-lg border border-[var(--color-line)] px-2.5 py-2 text-sm transition-colors disabled:opacity-50 xl:px-3 enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
          >
            <Download className="size-4" aria-hidden />
            <span className="hidden xl:inline">ダウンロード</span>
          </button>
          <button
            type="button"
            onClick={() => void deleteSelected()}
            disabled={selected.size === 0}
            aria-label="削除"
            className="flex shrink-0 items-center gap-1 rounded-lg bg-[var(--color-plum)] px-2.5 py-2 text-sm text-white transition disabled:opacity-50 xl:px-3 enabled:hover:opacity-85"
          >
            <Trash2 className="size-4" aria-hidden />
            <span className="hidden xl:inline">削除</span>
          </button>
        </div>
      )}

      {picking && (
        <MonthSheet
          counts={monthCounts.data ?? new Map()}
          onPick={(picked) => {
            setPicking(false);
            setMonth(picked);
            window.scrollTo({ top: 0 });
          }}
          onClose={() => {
            setPicking(false);
          }}
        />
      )}

      {tagging && (
        // 付け外しは押すたびに効くので、閉じたところで選択も解く
        <TagPicker
          mediaIds={[...selected]}
          onClose={() => {
            setTagging(false);
            leaveSelectMode();
          }}
        />
      )}

      {detailId !== null && (
        <MediaDetail
          items={items}
          currentId={detailId}
          onChange={setDetailId}
          onClose={() => {
            setDetailId(null);
          }}
          thumbnailUrls={thumbUrls}
          onReachEnd={loadMore}
          total={total.data}
        />
      )}
    </div>
  );
}
